import React from "react";
import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Input,
  Chip,
} from "@material-ui/core";
import { COMMON_ALLERGIES, COMMON_HEALTH_CONDITIONS } from "../../../utils/HealthProfileConstants";

interface HealthProfileSectionProps {
  gender: string;
  commonAllergies: string[];
  commonHealthConditions: string[];
  pregnantOrNursing: boolean;
  onGenderChange: (value: string) => void;
  onCommonAllergiesChange: (value: string[]) => void;
  onCommonHealthConditionsChange: (value: string[]) => void;
  onPregnantOrNursingChange: (value: boolean) => void;
}

const HealthProfileSection: React.FC<HealthProfileSectionProps> = ({
  gender,
  commonAllergies,
  commonHealthConditions,
  pregnantOrNursing,
  onGenderChange,
  onCommonAllergiesChange,
  onCommonHealthConditionsChange,
  onPregnantOrNursingChange,
}) => {
  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
          Health Profile
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth variant="outlined">
          <InputLabel>Gender</InputLabel>
          <Select
            value={gender}
            onChange={(e) => onGenderChange(e.target.value as string)}
            label="Gender"
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="allergies-multiple-chip-label">
            Common Allergies
          </InputLabel>
          <Select
            labelId="allergies-multiple-chip-label"
            id="allergies-multiple-chip"
            multiple
            value={commonAllergies}
            onChange={(e) => onCommonAllergiesChange(e.target.value as string[])}
            input={<Input id="select-multiple-chip" />}
            renderValue={(selected) => (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {(selected as string[]).map((id) => {
                  const allergy = COMMON_ALLERGIES.find((a) => a.id === id);
                  return allergy ? (
                    <Chip key={id} label={allergy.name} size="small" />
                  ) : null;
                })}
              </div>
            )}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
              PaperProps: {
                style: {
                  maxHeight: 300,
                  marginTop: 16,
                },
              },
            }}
          >
            {COMMON_ALLERGIES.map((allergy) => (
              <MenuItem key={allergy.id} value={allergy.id}>
                {allergy.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="health-conditions-multiple-chip-label">
            Common Health Conditions
          </InputLabel>
          <Select
            labelId="health-conditions-multiple-chip-label"
            id="health-conditions-multiple-chip"
            multiple
            value={commonHealthConditions}
            onChange={(e) =>
              onCommonHealthConditionsChange(e.target.value as string[])
            }
            input={<Input id="select-multiple-chip-conditions" />}
            renderValue={(selected) => (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {(selected as string[]).map((id) => {
                  const condition = COMMON_HEALTH_CONDITIONS.find(
                    (c) => c.id === id
                  );
                  return condition ? (
                    <Chip key={id} label={condition.name} size="small" />
                  ) : null;
                })}
              </div>
            )}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              getContentAnchorEl: null,
              PaperProps: {
                style: {
                  maxHeight: 300,
                  marginTop: 16,
                },
              },
            }}
          >
            {COMMON_HEALTH_CONDITIONS.map((condition) => (
              <MenuItem key={condition.id} value={condition.id}>
                {condition.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {gender === "Female" && (
        <Grid item xs={12} sm={6}>
          <FormControl
            component="fieldset"
            fullWidth
            style={{ marginTop: 8 }}
          >
            <FormLabel component="legend" style={{ fontSize: "0.875rem" }}>
              Pregnant or Nursing
            </FormLabel>
            <RadioGroup
              aria-label="pregnantOrNursing"
              name="pregnantOrNursing"
              value={pregnantOrNursing ? "Yes" : "No"}
              onChange={(e) => onPregnantOrNursingChange(e.target.value === "Yes")}
              row
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label="No"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
    </>
  );
};

export default HealthProfileSection;
